// Generic action resolver: maps device state onto typed logical actions
// through a binding table. Knows nothing about amoeba verbs (those live in
// src/game/controls.ts) and nothing about the DOM (devices implement
// DeviceSnapshot, so the whole resolver runs headless in vitest).
//
// Two consumption mechanisms and no more: buffered actions are consumed on
// fire (consume()), and a context switch raises a barrier that invalidates
// everything pressed before it — a Space pressed inside a menu can't dash on
// close. Held state always polls the live device, so a W held across a menu
// resumes walking the moment the menu closes.

import type { Vec2 } from './deadzone'

export type InputContext = 'gameplay' | 'menu'
export type DeviceKind = 'kbm' | 'pad'

export type Binding =
  | { key: string } // KeyboardEvent.code
  | { mouseButton: number } // 0 LMB · 1 MMB · 2 RMB
  | { padButton: number } // standard-mapping index (never 16 — guide is unreliable)
  | { keyVector: { up: string[]; down: string[]; left: string[]; right: string[] } }
  | { stick: 'left' | 'right' }
  | { padDpad: true } // buttons 12–15 (up/down/left/right), composited like keyVector

export interface ButtonDef {
  /** 'any' matches in every context (e.g. wheel zoom, which works while menus freeze the sim). */
  context: InputContext | 'any'
  /** Press-buffer window: an early press fires when the gate opens. */
  bufferMs?: number
  bindings: Binding[]
}

export interface VectorDef {
  /** 'any' matches in every context (e.g. wheel zoom, which works while menus freeze the sim). */
  context: InputContext | 'any'
  bindings: Binding[]
}

export interface ActionDefs<B extends string, V extends string> {
  buttons: Record<B, ButtonDef>
  vectors: Record<V, VectorDef>
}

/** What a device exposes per frame. Fakeable in tests; foreign bindings are inert. */
export interface DeviceSnapshot {
  readonly kind: DeviceKind
  isDown(b: Binding): boolean
  /** ms timestamp of the most recent press edge for this binding; -Infinity if never. */
  pressedAt(b: Binding): number
  /** Analog/composite vector for this binding, or null if the binding isn't this device's. */
  vector(b: Binding): Vec2 | null
  /** ms timestamp of the last meaningful activity (mapped, past-deadzone — drives lastDevice). */
  readonly activityAt: number
}

export interface Actions<B extends string, V extends string> {
  held(id: B): boolean
  /** Press edge this frame (context-filtered). */
  justPressed(id: B): boolean
  /** Unconsumed press within the buffer window (def.bufferMs unless overridden). */
  justPressedWithin(id: B, ms?: number): boolean
  /** Press edge this frame ignoring context — escape hatch for panel-close keys. */
  pressedRaw(id: B): boolean
  /** Eat the buffered press after acting on it, so one press can't fire twice. */
  consume(id: B): void
  vec(id: V): Vec2
  setContext(ctx: InputContext): void
  readonly context: InputContext
  readonly lastDevice: DeviceKind
  /** Called once per frame by the input composer before systems run. */
  update(devices: readonly DeviceSnapshot[], nowMs: number, focused: boolean): void
}

const ZERO: Vec2 = { x: 0, y: 0 }

export function createActions<B extends string, V extends string>(
  defs: ActionDefs<B, V>,
): Actions<B, V> {
  let devices: readonly DeviceSnapshot[] = []
  let now = 0
  let prevNow = -1
  // Presses at or before the barrier don't count (context switch, focus loss).
  let barrier = -1
  let context: InputContext = 'gameplay'
  let lastDevice: DeviceKind = 'kbm'
  let lastActivityAt = -1
  const consumedAt = new Map<B, number>()

  const latestPress = (id: B): number => {
    let t = -Infinity
    for (const b of defs.buttons[id].bindings) {
      for (const d of devices) {
        const p = d.pressedAt(b)
        if (p > t) t = p
      }
    }
    return t
  }

  const pressOk = (id: B, since: number): boolean => {
    const t = latestPress(id)
    return t > since && t > barrier && t > (consumedAt.get(id) ?? -Infinity)
  }

  return {
    get context() {
      return context
    },
    get lastDevice() {
      return lastDevice
    },
    update(d, nowMs, focused) {
      devices = d
      prevNow = now
      now = nowMs
      if (!focused) barrier = now
      for (const dev of devices) {
        if (dev.activityAt > lastActivityAt) {
          lastActivityAt = dev.activityAt
          lastDevice = dev.kind
        }
      }
    },
    held(id) {
      const ctx = defs.buttons[id].context
      if (ctx !== 'any' && ctx !== context) return false
      return defs.buttons[id].bindings.some(b => devices.some(d => d.isDown(b)))
    },
    justPressed(id) {
      const ctx = defs.buttons[id].context
      return (ctx === 'any' || ctx === context) && pressOk(id, prevNow)
    },
    justPressedWithin(id, ms) {
      const ctx = defs.buttons[id].context
      if (ctx !== 'any' && ctx !== context) return false
      const window = ms ?? defs.buttons[id].bufferMs ?? 0
      return pressOk(id, window > 0 ? now - window : prevNow)
    },
    pressedRaw(id) {
      return pressOk(id, prevNow)
    },
    consume(id) {
      consumedAt.set(id, now)
    },
    vec(id) {
      const ctx = defs.vectors[id].context
      if (ctx !== 'any' && ctx !== context) return ZERO
      // Greatest actuation wins (Unity-style conflict resolution) — silently
      // handles stick + WASD held at once without explicit device switching.
      let best = ZERO
      let bestLen = 0
      for (const b of defs.vectors[id].bindings) {
        for (const d of devices) {
          const v = d.vector(b)
          if (!v) continue
          const len = Math.hypot(v.x, v.y)
          if (len > bestLen) {
            best = v
            bestLen = len
          }
        }
      }
      return best
    },
    setContext(ctx) {
      if (ctx === context) return
      context = ctx
      barrier = now
    },
  }
}
